import React, { Suspense, useEffect, useState } from 'react';

import * as dayjs from 'dayjs'
import * as dayjs_plugin_utc from 'dayjs/plugin/utc'

import { getEventsByCatalog } from '../../../api/kquake'
import { LoadingScreen } from '../../LoadingScreen'
import { useTranslation } from 'react-i18next';

dayjs.extend(dayjs_plugin_utc)


const Plot = React.lazy(() => import('react-plotly.js'));


const UNDETERMINED_EARTHQUAKE_MAGNITUDE_DISPLAY_MAGNITUDE = 0.5

const EVENT_CATALOG_INFO = {
  // GYEONGJU2016: {title: '2016 경주 지진', cite: 'Son et al. 2018', theFirstEvent: dayjs('2016-09-12T10:44:33.298')},
  GYEONGJU2016: {
    title: 'GyeongjuEarthquake2016',
    theFirstEvent: dayjs('2016-09-12T10:44:33.298'),
    boundary: {
      maxLat: 35.78966 + 0.02,  // 35.80966
      minLat: 35.74478 - 0.02,  // 35.72478
      maxLon: 129.2048 + 0.02,  // 129.2248
      minLon: 129.1685 - 0.02,  // 129.1485
      minDepth: 11.363,
      maxDepth: 15.941,
    },
    dtick: 0.01,
    aspectratio: {
      x: 1, // (129.2048 - 129.1685) * 89 = 3.2307,
      y: 1.54, // (35.78966 - 35.74478) * 111 = 4.98168, 4.98168 / 3.2307 = 1.54
      z: 1.42 // 15.941 - 11.363 = 4.578, 4.578 / 3.2307 = 1.42
    },
    magnitudeAmplifier: {
      d2: 4,
      d3: 2.5,
    },
    citeShort: 'Son et al. 2018',
    cite: 'Son, M., Cho, C. S., Shin, J. S., Rhee, H. M., & Sheen, D. H. (2018). Spatiotemporal Distribution of Events during the First Three Months of the 2016 Gyeongju, Korea, Earthquake Sequence. Bulletin of the Seismological Society of America, 108(1), 210-217.',
  },
  POHANG2017: {
    title: 'PohangEarthquake2017',
    theFirstEvent: dayjs('2017-11-14T20:04:17.68'),
    boundary: {
      maxLat: 36.1483 + 0.005,
      minLat: 36.0643 - 0.005,
      maxLon: 129.3963 + 0.005,
      minLon: 129.3148 - 0.005,
      minDepth: 2.51,
      maxDepth: 7.4,
    },
    aspectratio: {
      x: 1.48, // (129.3963 - 129.3148) * 89 = 7.2535, 7.2535 / 4.89 = 1.48
      y: 1.91, // (36.1483 - 36.0643) * 111 = 9.324, 9.324 / 4.89 = 1.91
      z: 1, // 7.4 - 2.51 = 4.89, 
    },
    magnitudeAmplifier: {
      d2: 4,
      d3: 2.5,
    },
    dtick: 0.02,
    citeShort: 'Son et al. 2020',
    cite: 'Son, M., Cho, C. S., Lee, H. K., Han, M., Shin, J. S., Kim, K., & Kim, S. (2020). Partitioned fault movement and aftershock triggering: evidence for fault interactions during the 2017 Mw 5.4 Pohang earthquake, South Korea. Journal of Geophysical Research: Solid Earth, 125(12), e2020JB020005.',
  },
  HAENAM2020: {
    title: 'HaenamEarthquake2020',
    theFirstEvent: dayjs('2020-04-25T12:31:27'),
    boundary: {
      maxLat: 34.65991 + 0.001,
      minLat: 34.6583 - 0.001,
      maxLon: 126.40009 + 0.001,
      minLon: 126.39616 - 0.001,
      minDepth: 20.156,
      maxDepth: 20.626,
    },
    aspectratio: {
      x: 1.96, // (126.40009 - 126.39616) * 89 = 0.34977, 0.34977 / 0.17871 = 1.96
      y: 1, // (34.65991 - 34.6583) * 111 = 0.17871, 
      z: 2.63, // 20.626 - 20.156 = 0.47, 0.47 / 0.17871 = 2.63
    },
    magnitudeAmplifier: {
      d2: 5,
      d3: 3.5,
    },
    dtick: 0.001,
    citeShort: 'Son et al. 2021',
    cite: 'Son, M., Cho, C. S., Choi, J. H., Jeon, J. S., & Park, Y. K. (2021). Spatiotemporal patterns of the 2020 Haenam earthquake sequence, South Korea: lineament and migration implying fluid-driven earthquake swarm. Geosciences Journal, 25(1), 19-31.',
  },
};


const _get_aspectratio = (boundaries) => {
  const x_in_km = Math.abs((boundaries.maxLon - boundaries.minLon) * 89)
  const y_in_km = Math.abs((boundaries.maxLat - boundaries.minLat) * 111)
  const z_in_km = Math.abs(boundaries.maxDepth - boundaries.minDepth)

  const min_km = Math.min(x_in_km, y_in_km, z_in_km)
  return {
    x: x_in_km / min_km,
    y: y_in_km / min_km,
    z: z_in_km / min_km,
  }
}


const getNorthArrowTraces = ({minLat, maxLat, minLon, maxLon, minDepth}) => {
  // const ARROW_TICK_SIZE = 0.001;
  const ARROW_SIZE = 3;
  const ARROW_MARGIN = 3;
  // const latitudes = earthquakes.map(({latitude}) => latitude);
  // const longitudes = earthquakes.map(({longitude}) => longitude);
  // const maxLat = Math.max(...latitudes);
  // const minLat = Math.min(...latitudes);
  // const maxLon = Math.max(...longitudes);
  // const minLon = Math.min(...longitudes);
  // const minDepth = Math.min(...earthquakes.map(({depth}) => depth));
  // console.log(maxLat, minLat, maxLon, minLon, minDepth, maxDepth);
  const zValue = Math.floor(minDepth) * -1;

  const xTickSize = (maxLon - minLon) / 50.;
  const yTickSize = (maxLat - minLat) / 50.;

  const x = [];
  const y = [];

  x.push(minLon + (xTickSize * (ARROW_MARGIN + ARROW_SIZE)));
  y.push(maxLat - (yTickSize * ARROW_MARGIN));

  x.push(minLon + (xTickSize * ARROW_MARGIN));
  y.push(maxLat - (yTickSize * (ARROW_MARGIN + (ARROW_SIZE * 2))));

  x.push(minLon + (xTickSize * (ARROW_MARGIN + ARROW_SIZE)));
  y.push(maxLat - (yTickSize * (ARROW_MARGIN + (ARROW_SIZE * 1.5))));

  x.push(minLon + (xTickSize * (ARROW_MARGIN + (ARROW_SIZE * 2))));
  y.push(maxLat - (yTickSize * (ARROW_MARGIN + (ARROW_SIZE * 2))));

  x.push(minLon + (xTickSize * (ARROW_MARGIN + ARROW_SIZE)));
  y.push(maxLat - (yTickSize * ARROW_MARGIN));

  // x.push(minLon + margin + arrowSize);
  // y.push(maxLat - margin);

  // x.push(minLon + margin);
  // y.push(maxLat - (margin + (arrowSize * 2)));

  return [{
    type: 'scatter3d',
    showlegend: false,
    x,
    y,
    z: new Array(x.length).fill(zValue),
    mode: 'lines',
    hoverinfo: 'none',
    line: {
      color: 'black',
      width: 4,
    },
  }, {
    type: 'scatter3d',
    showlegend: false,
    x: [x[0]],
    y: [y[0] + yTickSize],
    z: [zValue],
    mode: 'text',
    text: 'N',
    textposition: 'middle center',
    hoverinfo: 'none',
    textfont: {
      size: 18,
    }
  }];
}


export const ClusterEarthquake = () => {
  const { t } = useTranslation();
  const [canvasSize, setCanvasSize] = useState([0, 0]);
  // const [canvasSize, setCanvasSize] = useState({
  //   scatterWidth: 0, scatterHight: 0, scatter3DWidth: 0, scatter3DHight: 0,
  // });
  const [colorByElapsed, setColorByElapsed] = useState(false);

  useEffect(() => {
    if (window.innerWidth < 576) {
      const canvasWidth = window.innerWidth - 10;
      setCanvasSize([canvasWidth, (canvasWidth * 1.5)]);
    } else if (window.innerWidth < 768) {
      const canvasWidth = window.innerWidth - 40;
      setCanvasSize([canvasWidth, (canvasWidth * 1.2)]);
    } /* else if (window.innerWidth < 992) {
      const canvasWidth = window.innerWidth - 60;
      setCanvasSize([canvasWidth, (canvasWidth * 1.3)]);
    } */ else {
      const canvasWidth = (window.innerWidth < 960) ? window.innerWidth - 60 : 900;      
      setCanvasSize([canvasWidth, (canvasWidth * 0.8)]);
    }
  }, []);

  const [eventCatalog, setEventCatalog] = useState('')
  // const [eventCatalogSelector, setEventCatalogSelector] = useState('GYEONGJU2016')
  const [earthquakes, setEarthquakes] = useState([]);
  const [earthquakeCache, setEarthquakeCache] = useState({});
  // const [eventCustomFilter, setEventCustomFilter] = useState(defaultCustomFilterState)
  const [isLoading, setIsLoading] = useState(false)

  const loadEarthquakes = async () => {
    const selectedElement = document.querySelector('input[name="3d-events-browser.catalog"]:checked');
    if (!selectedElement) {
      alert(t('page.major-earthquakes.selectAnEarthquake'))
      return
    }
    const catalog = selectedElement.value
    if (earthquakeCache[catalog]) {
      setEventCatalog(catalog)
      setEarthquakes(earthquakeCache[catalog])
    } else {
      setIsLoading(true)
      try {
        const responseData = await getEventsByCatalog(catalog)
        const _earthquakes = responseData.map((earthquake) => {
          const time = dayjs.unix(earthquake.origin.time);
          if (!time.isValid()) {
            console.error(`Failed to parse time string: ${earthquake.origin.time}`)
          }
          const elapsed = parseInt((time.unix() - EVENT_CATALOG_INFO[catalog].theFirstEvent.unix()) / 86400);
          return {
            time,
            latitude: earthquake.origin.latitude,
            longitude: earthquake.origin.longitude,
            depth: earthquake.origin.depth,
            mag: earthquake.magnitude._representative ? earthquake.magnitude[earthquake.magnitude._representative] : null,
            magType: earthquake.magnitude._representative,
            elapsed,
          };
        })
        setEventCatalog(catalog)
        setEarthquakes(_earthquakes)
        setEarthquakeCache({...earthquakeCache, [catalog]: _earthquakes})
        setIsLoading(false)
      } catch (error) {
        setIsLoading(false)
        if (error.httpStatus === 204) {
          alert(t('form.message.noContent'))
        }
        else {
          const alertMessage = [
            t('form.message.failedToRetrieve'),
            `Error message: ${error.message}`,
            t('form.message.contactAdministrator'),
          ]
          alert(alertMessage.join('\r\n'))
        }
      }
    }
  }

  const earthquake2DTrace = eventCatalog ? {
    type: 'scatter',
    name: '지진 이벤트',
    showlegend: false,
    x: earthquakes.map(({longitude}) => longitude),
    y: earthquakes.map(({latitude}) => latitude),
    mode: 'markers',
    marker: {
      size: earthquakes.map(({mag}) => Math.round((mag ? mag : UNDETERMINED_EARTHQUAKE_MAGNITUDE_DISPLAY_MAGNITUDE) * EVENT_CATALOG_INFO[eventCatalog].magnitudeAmplifier.d2)),
      color: earthquakes.map(({depth}) => depth),
      colorscale: 'Viridis',  // https://plotly.com/javascript/colorscales/
      opacity: 0.8,
      reversescale: true,
      showscale: true,
      colorbar: {
        title: {
          text: 'Depth (km)',
          side: 'top',
        },
        orientation: 'h',
        y: -0.3,
      },
      symbol: 'circle-open',
    },
    text: earthquakes.map((earthquake) => [
      `Time: ${earthquake.time.format('YYYY-MM-DDTHH:mm:ssZ')}`,
      (earthquake.mag ? `Magnitude: ${earthquake.mag} (${earthquake.magType})` : 'Magnitude: 미결정'),
      `Latitude: ${earthquake.latitude}`,
      `Longitude: ${earthquake.longitude}`,
      `Depth(km): ${earthquake.depth}`,
    ].join('<br />')),
    hoverinfo: 'text',
  } : null;
  const plotly2DLayout = eventCatalog ? {
    width: canvasSize[0],
    height: canvasSize[1],
    xaxis: {
      title: {text: 'Longitude'},
      dtick: EVENT_CATALOG_INFO[eventCatalog].dtick,
    },
    yaxis: {
      title: {text: 'Latitude'},
      dtick: EVENT_CATALOG_INFO[eventCatalog].dtick,
      scaleanchor: 'x',
      scaleratio: 1.247,
    },    
  } : null;

  const earthquake3DTrace = eventCatalog ? {
    type: 'scatter3d',
    name: '지진 이벤트',
    showlegend: false,
    x: earthquakes.map(({longitude}) => longitude),
    y: earthquakes.map(({latitude}) => latitude),
    z: earthquakes.map(({depth}) => depth * -1),
    mode: 'markers',
    marker: {
      size: earthquakes.map(({mag}) => Math.round((mag ? mag : UNDETERMINED_EARTHQUAKE_MAGNITUDE_DISPLAY_MAGNITUDE) * EVENT_CATALOG_INFO[eventCatalog].magnitudeAmplifier.d3)),
      color: colorByElapsed ? earthquakes.map(({elapsed}) => elapsed) : earthquakes.map(({depth}) => depth),
      colorscale: 'Viridis',  // https://plotly.com/javascript/colorscales/
      // opacity: 0.8,
      // cmin: colorByElapsed ? 0 : null,
      // cmax: colorByElapsed ? 8 : null,
      reversescale: !colorByElapsed,
      colorbar: {
        title: {
          text: colorByElapsed ? `Elapsed time from the first event: ${EVENT_CATALOG_INFO[eventCatalog].theFirstEvent.format('YYYY-MM-DDTHH:mm:ss UTC')} (days)` : 'Depth (km)',
          side: 'top',
        },
        len: 0.8,
        orientation: 'h',
        y: -0.2,
      },
      line: {
        color: colorByElapsed ? earthquakes.map(({elapsed}) => elapsed) : earthquakes.map(({depth}) => depth),
        colorscale: 'Viridis',  // https://plotly.com/javascript/colorscales/
        reversescale: !colorByElapsed,
      },
      symbol: 'circle-open',
    },
    text: earthquakes.map((earthquake) => [
      `Time: ${earthquake.time.format('YYYY-MM-DDTHH:mm:ssZ')}`,
      (earthquake.mag ? `Magnitude: ${earthquake.mag} (${earthquake.magType})` : 'Magnitude: 미결정'),
      `Latitude: ${earthquake.latitude}`,
      `Longitude: ${earthquake.longitude}`,
      `Depth(km): ${earthquake.depth}`,
    ].join('<br />')),
    hoverinfo: 'text',
  } : null;
  const northArrowTraces = eventCatalog ? getNorthArrowTraces(EVENT_CATALOG_INFO[eventCatalog].boundary) : null;
  const plotly3DLayout = eventCatalog ? {
    // autosize: true,
    width: canvasSize[0],
    height: canvasSize[1],
    // title: {
    //   text: '경주 지진 (2016)',
    //   font: {
    //     size: 26,
    //   },
    //   y: 0.9,
    // },

    scene: {
      aspectmode: 'manual',
      aspectratio: _get_aspectratio(EVENT_CATALOG_INFO[eventCatalog].boundary),
      xaxis: {
        title: {text: 'Longitude'},
        dtick: EVENT_CATALOG_INFO[eventCatalog].dtick,
        range: [EVENT_CATALOG_INFO[eventCatalog].boundary.minLon, EVENT_CATALOG_INFO[eventCatalog].boundary.maxLon],
      },
      yaxis: {
        title: {text: 'Latitude'},
        dtick: EVENT_CATALOG_INFO[eventCatalog].dtick,
        range: [EVENT_CATALOG_INFO[eventCatalog].boundary.minLat, EVENT_CATALOG_INFO[eventCatalog].boundary.maxLat],
      },
      zaxis: {
        title: {text: 'Depth (km)'},
      },
    },
  } : null;

  return (
    <div className="container">
      <div className="sub-container">
        <div style={{fontSize: "15px", marginBottom: "5px"}}>
          &nbsp;&nbsp;<i className="ion ion-md-help-circle-outline"></i>
          {t('page.major-earthquakes.desc')}
        </div>
        <div className="overflow-auto">
          <table className="datatable mb20">
            <colgroup className=""><col style={{width: '20%'}} /><col style={{width: '80%'}} /></colgroup>
            <tbody>
            <tr>
                <th className="first">{t('w.earthquake')}</th>
                <td className="first clearfix">
                  <div className="left">
                    {Object.keys(EVENT_CATALOG_INFO).map(catalog => 
                      <label key={catalog} className="datatable_select_radio" htmlFor={`3d-events-browser.${catalog}`}>
                        <input type="radio" id={`3d-events-browser.${catalog}`} 
                          name='3d-events-browser.catalog' value={catalog} className="with-gap"
                          // checked={eventCatalogSelector === catalog} onChange={e => {if (e.target.checked) {setEventCatalogSelector(catalog)}}}
                        />
                        <span>{t('page.major-earthquakes.' + EVENT_CATALOG_INFO[catalog].title)} &nbsp;&nbsp;&nbsp;&nbsp;</span>
                      </label>  
                    )}
                  </div>                  
                </td>
              </tr>             
            </tbody>
          </table>

          <div className="center-align row">
            <button type="button" className="waves-effect waves-light button-lg btn-block" onClick={loadEarthquakes}>
              {t('w.search')}
            </button>
          </div>
        </div>
      </div>
      {(eventCatalog && EVENT_CATALOG_INFO[eventCatalog]) && 
        <div style={{width: canvasSize[0], marginTop: 20, marginLeft: 'auto', marginRight: 'auto', textAlign: 'center'}}>
          <h4 style={{textAlign: 'center'}}>
            {
              t('page.major-earthquakes.' + EVENT_CATALOG_INFO[eventCatalog].title) + ' ' +
              t('page.major-earthquakes.andAftershocks')
            }
          </h4>
          {(earthquakes.length > 0) && 
          <Suspense fallback={<div>Loading...</div>}>
            <Plot
              data={[earthquake2DTrace]}        
              layout={plotly2DLayout}
            />
            <br />
            <br />
            <Plot
              data={[earthquake3DTrace, ...northArrowTraces]}        
              layout={plotly3DLayout}
            />
            <label className="datatable_select_radio" htmlFor="event3dDisplay.color.enabled">
              <input 
                type="checkbox" id="event3dDisplay.color.enabled" className="filled-in with-gap"
                name="color.enabled" checked={colorByElapsed} onChange={e => {setColorByElapsed(e.target.checked)}}
              />
              <span>{t('page.major-earthquakes.colorMarkersByElapsedTime')}</span>
            </label>
          </Suspense>}
          <br /><br /><br />
          <span><strong>Reference</strong>: {EVENT_CATALOG_INFO[eventCatalog].cite}</span>
          <br /><br />
        </div>
      }
      <LoadingScreen screen={isLoading}/>
    </div>
  );
}